import React, { useEffect, useState } from 'react';

import { Divider, Tab, Tabs, Toolbar } from '@mui/material';
import clsx from 'clsx';
import { Spacer, TabPanel } from 'src/components/generic';
import { Queries } from 'src/editor/queries';
import {
  EditingModeSelect,
  InsertButtons,
  LayoutButtons,
  ReviewButtons,
  TableButtons,
  ToolbarMenus,
} from 'src/editor/toolbars';
import { useWalkthroughActive } from 'src/editor/toolbars/editor-app-bars/helpers';
import { useEditorViewPreferences } from 'src/editor/vars';
import { useCurrentUserId } from 'src/gql';
import { useEditorLoading, useSlateSelector } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { useTabStyles } from 'src/styles';
import { makeStyles } from 'tss-react/mui';

import { HomeTabBar } from './tabs/home-tab';
import { ToolbarDialogs } from './ToolbarDialogs';

const useStyles = makeStyles()(theme => ({
  divider: {
    height: 24,
    marginLeft: theme.spacing(0.8),
    marginRight: theme.spacing(0.8),
  },
  hidden: {
    margin: theme.spacing(0, 0, 1, 1),
  },
  horizontalDivider: {
    backgroundColor: theme.palette.divider,
    height: 2,
  },
  tabsWrapper: {
    display: 'flex',
  },
  toolbar: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px ${theme.appColors.rule} solid`,
    paddingTop: theme.spacing(1),
    zIndex: theme.zIndex.appBar,
  },
  toolbarTabs: {
    marginLeft: theme.spacing(3),
    minHeight: 24,
  },
  vertDivider: {
    alignSelf: 'center',
    height: 18,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));

export const editorToolbarId = 'editor-toolbar';

export const EditorToolbar = React.memo(function EditorToolbarComponent(): JSX.Element | null {
  const { classes } = useStyles();
  const { classes: tabClasses } = useTabStyles();
  const currentUserId = useCurrentUserId();
  const editorLoading = useEditorLoading();
  const { commenting, viewing } = useEditingMode();
  const { walkThroughActive } = useWalkthroughActive();
  const [activeTab, setActiveTab] = useState<number>(0);
  const { fullScreen } = useEditorViewPreferences();
  const hide = !currentUserId || viewing || commenting;

  if (fullScreen || walkThroughActive) return null;

  return (
    <div className={classes.toolbar}>
      <div className={clsx(classes.tabsWrapper, hide && classes.hidden)}>
        {!hide && (
          <>
            <Tabs
              classes={{ root: clsx(tabClasses.tabsRoot, classes.toolbarTabs) }}
              indicatorColor="primary"
              scrollButtons={false}
              value={activeTab}
              variant="scrollable"
              onChange={(_, i) => setActiveTab(i)}
            >
              <Tab
                classes={{ root: tabClasses.tabRoot }}
                data-cy="home-tab"
                disabled={editorLoading}
                label="Home"
              />
              <Tab
                classes={{ root: tabClasses.tabRoot }}
                data-cy="insert-tab"
                disabled={editorLoading}
                label="Insert"
              />
              <Tab
                classes={{ root: tabClasses.tabRoot }}
                data-cy="layout-tab"
                disabled={editorLoading}
                label="Layout"
              />
              <Tab
                classes={{ root: tabClasses.tabRoot }}
                data-cy="review-tab"
                disabled={editorLoading}
                label="Review"
              />
              <TableTab activeTab={activeTab} setActiveTab={setActiveTab} />
            </Tabs>
            <Divider className={classes.vertDivider} orientation="vertical" />
          </>
        )}
        <ToolbarMenus />
        <ToolbarDialogs />
        <Spacer />
        <EditingModeSelect />
      </div>
      {!hide && (
        <>
          <TabPanel tabValue={0} value={activeTab}>
            <Toolbar id={editorToolbarId} variant="dense">
              <HomeTabBar />
            </Toolbar>
          </TabPanel>
          <TabPanel tabValue={1} value={activeTab}>
            <Toolbar variant="dense">
              <InsertButtons />
            </Toolbar>
          </TabPanel>
          <TabPanel tabValue={2} value={activeTab}>
            <Toolbar variant="dense">
              <LayoutButtons />
            </Toolbar>
          </TabPanel>
          <TabPanel tabValue={3} value={activeTab}>
            <Toolbar variant="dense">
              <ReviewButtons />
            </Toolbar>
          </TabPanel>
          <TabPanel tabValue={4} value={activeTab}>
            <Toolbar variant="dense">
              <TableButtons />
            </Toolbar>
          </TabPanel>
        </>
      )}
    </div>
  );
});

export function TableTab({
  activeTab,
  setActiveTab,
  ...rest
}: {
  activeTab: number;
  setActiveTab: (activeTab: number) => void;
}): JSX.Element | null {
  const { classes: tabClasses } = useTabStyles();
  const editorLoading = useEditorLoading();
  const isSelectionWithinTable = useSlateSelector(
    editor => Queries.getIsSelectionWithinTable(editor).isSelectionWithinTable
  );

  useEffect(() => {
    if (!isSelectionWithinTable && activeTab === 4) setActiveTab(0);
  }, [isSelectionWithinTable, activeTab, setActiveTab]);

  if (!isSelectionWithinTable) return null;

  return (
    <Tab
      classes={{ root: tabClasses.tabRoot }}
      data-cy="table-tab"
      disabled={editorLoading}
      label="Table"
      {...rest}
    />
  );
}
